import {PHASES, WAR_PHASE_STAGES} from "../../../constants/phases";
import {
  selectCurrentPlayer,
  selectGameResults,
  selectIsGameStarted,
  selectPhase,
  selectPlayers,
  selectSharedGameState,
  selectWarPhaseStage
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {checkIfParallelSupplyPhase, checkIfPlayerCompletedPhase} from "../supply/parallelSupply";
import {getSupplyFarms} from "./getSupplyFarms";
import {getConstructionActions} from "./getConstructionActions";
import {getAbilityUnits, getManeuverUnits, getMovementUnits, getShootingUnits} from "./getWarStageUnits";

const NO_IDS = [];

const WAR_STAGE_UNITS = {
  [WAR_PHASE_STAGES.MOVEMENT]: getMovementUnits,
  [WAR_PHASE_STAGES.ABILITIES]: getAbilityUnits,
  [WAR_PHASE_STAGES.SHOOTING]: getShootingUnits,
  [WAR_PHASE_STAGES.MANEUVER]: getManeuverUnits,
};

// Идет ли сейчас ход игрока: в одновременной фазе снабжения ходят все, кто
// еще не закончил, иначе — только текущий игрок
const checkIfPlayerTurn = (state, playerId) => {
  const gameState = selectSharedGameState(state);
  if (checkIfParallelSupplyPhase(gameState)) {
    return !checkIfPlayerCompletedPhase(gameState, playerId);
  }
  return selectCurrentPlayer(state)?.id === playerId;
};

// Ид оставшихся действий строительства: у действия нет своего объекта на поле
const getConstructionActionIds = (used, total) => Array.from(
  {length: Math.max(0, total - used)},
  (item, index) => `construction_action_${used + index}`
);

// Пропустивший ход отряд обработан (spec phase-progress), даже если ему еще
// есть что делать: он уходит из способных и бездействующих в обработанные.
// Отряд, который пропустил ход, а потом действовал, уже обработан — дважды не
// считается
const applySkips = ({processedIds, actionableIds, idleIds}, skippedUnitIds) => {
  if (!skippedUnitIds.length) {
    return {processedIds, actionableIds, idleIds};
  }
  const skipped = new Set(skippedUnitIds);
  return {
    processedIds: [
      ...processedIds,
      ...actionableIds.filter((id) => skipped.has(id)),
      ...idleIds.filter((id) => skipped.has(id)),
    ],
    actionableIds: actionableIds.filter((id) => !skipped.has(id)),
    idleIds: idleIds.filter((id) => !skipped.has(id)),
  };
};

const getPhaseDeeds = (state, phase, warPhaseStage, player, skippedUnitIds) => {
  if (phase === PHASES.SUPPLY) {
    const {processedIds, actionableIds} = getSupplyFarms(state, player);
    return {done: processedIds.length, actionableIds, idleCount: 0};
  }

  if (phase === PHASES.CONSTRUCTION) {
    const {used, total} = getConstructionActions(state, player);
    return {done: used, actionableIds: getConstructionActionIds(used, total), idleCount: 0};
  }

  const getStageUnits = phase === PHASES.WAR && WAR_STAGE_UNITS[warPhaseStage];
  if (getStageUnits) {
    const {processedIds, actionableIds, idleIds} = applySkips(getStageUnits(state, player), skippedUnitIds);
    // Отряд без действия входит в S только в перемещении; на остальных этапах
    // дело — лишь отряд с доступным действием
    const idleCount = warPhaseStage === WAR_PHASE_STAGES.MOVEMENT ? idleIds.length : 0;
    return {done: processedIds.length, actionableIds, idleCount};
  }

  // Найм, ближний бой и подготовка поля — без счетчика
  return null;
};

/**
 * Дела текущей фазы или этапа войны игрока (spec phase-progress):
 *  - done — N, сколько дел обработано; null, если счетчика на этом этапе нет
 *    (найм, ближний бой, подготовка, чужой ход, конец партии);
 *  - actionableIds — фермы, действия строительства или отряды, которые еще
 *    могут действовать. Пустой список — «делать больше нечего», даже если N < S;
 *  - total — S на текущем состоянии: done + actionableIds.length, а в
 *    перемещении еще и отряды, которым некуда идти. Число дел на начало этапа
 *    запоминает selectPhaseProgress.
 *
 * Чистая функция состояния: не требует отрисованного поля и одинаково
 * считается в браузере и на сервере. playerId передается явно — в
 * одновременной фазе снабжения и в hot-seat текущий игрок не равен действующему.
 *
 * skippedUnitIds — отряды, пропустившие ход на этапе войны: пропуски живут
 * только у клиента (selectSkippedUnitIds), поэтому приходят отдельным входом.
 * Без них (сервер, боты) пропуски не учитываются.
 */
export const getPhaseProgress = (state, playerId, {skippedUnitIds = NO_IDS} = {}) => {
  const phase = selectPhase(state);
  const warPhaseStage = phase === PHASES.WAR ? selectWarPhaseStage(state) : null;
  const noCounter = {phase, warPhaseStage, done: null, total: null, actionableIds: NO_IDS};

  if (!selectIsGameStarted(state) || selectGameResults(state)?.length > 0) {
    return noCounter;
  }

  const player = (selectPlayers(state) || []).find((item) => item.id === playerId);
  if (!player || !checkIfPlayerTurn(state, playerId)) {
    return noCounter;
  }

  const deeds = getPhaseDeeds(state, phase, warPhaseStage, player, skippedUnitIds);
  if (!deeds) {
    return noCounter;
  }

  return {
    phase,
    warPhaseStage,
    done: deeds.done,
    total: deeds.done + deeds.actionableIds.length + deeds.idleCount,
    actionableIds: deeds.actionableIds,
  };
};
